import { Button } from "@mantine/core";
import { useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ApiResponse } from "../../../api";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../mantine/notifications/useToast";
import { MeterEnergyMeasurementType } from "../../../types/api.types";
import type { WizardFormModeProps } from "../../../types/wizard.types";
import type { MeteringOrMarketLocation } from "../../../utils/backend-types";
import { applyHighlightingToObjectFormFieldData } from "../../../utils/react-hook-form/applyHighlightingToObjectFormFieldData";
import { resolveMeteringOrMarketLocationType } from "../../../utils/resolveMeteringOrMarketLocationType";
import type { CreatableTarget } from "../../BuildingBlocks/Forms/Controllers/CreatableFormFieldController";
import { CreatableFormFieldController } from "../../BuildingBlocks/Forms/Controllers/CreatableFormFieldController";
import { FormFieldController } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { FormSection } from "../../BuildingBlocks/Forms/FormSection/FormSection";
import { setErrorsFromResponseData } from "../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import type { MeterResponse } from "../Meter.types";
import {
  METER_FORM_DEFAULT_VALUES,
  METER_FORM_FIELD_DATA,
  METERING_COMPANY_HELP_TEXT,
  METERING_MSB_HELP_TEXT
} from "./MeterForm.constants";
import type { MeterFormData } from "./MeterForm.types";

type MeterFormProps = {
  locations: Array<MeteringOrMarketLocation>;
  onCancel: () => void;
  onChangeNumErrors: (numErrors: number) => void;
  onChangeNumHighlightedFields: (numHighlightedFields: number) => void;
  onSubmitCreate: (data: MeterFormData) => Promise<ApiResponse<MeterResponse>>;
  onSubmitUpdate: (
    data: MeterFormData,
    id: number
  ) => Promise<ApiResponse<MeterResponse>>;
  showStaffView?: boolean;
} & WizardFormModeProps<MeterResponse>;

function MeterForm({
  locations,
  showStaffView,
  onCancel,
  onChangeNumErrors,
  onChangeNumHighlightedFields,
  onSubmitCreate,
  onSubmitUpdate,
  ...modeProps
}: MeterFormProps) {
  const { showErrorToast } = useToast();
  const { t } = useTranslation();
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
    setError,
    setValue,
    watch
  } = useForm<MeterFormData>({
    defaultValues:
      modeProps.mode === "edit"
        ? modeProps.wizardObject
        : METER_FORM_DEFAULT_VALUES,
    // Hidden measurement_type-conditional fields (frequency / conversion_factor)
    // must drop out of both validation and the submitted payload when not shown.
    shouldUnregister: true
  });
  // A non-field server error leaves RHF's form errors empty, so its own
  // isSubmitSuccessful would report the failed submit as a success.
  const [hasSubmitSucceeded, setHasSubmitSucceeded] = useState(false);
  const { formFieldData, numHighlightedFields } =
    applyHighlightingToObjectFormFieldData(
      METER_FORM_FIELD_DATA,
      watch,
      modeProps.mode === "edit" ? modeProps : undefined
    );

  const isArbitraryCumulative =
    watch("measurement_type") ===
    MeterEnergyMeasurementType.ArbitraryCumulative;

  const locationChoices = locations.map((location) => ({
    value: location.id,
    label: location.name
  }));

  // Both Einspeisung and Entnahme select from the same merged location list,
  // so they share the same union target and resolver.
  const locationTarget: CreatableTarget = {
    oneOf: ["meteringLocation", "marketLocation"],
    resolve: (id) => {
      const location = locations.find((candidate) => candidate.id === id);

      return location
        ? resolveMeteringOrMarketLocationType(location.number)
        : undefined;
    }
  };

  useEffect(() => {
    onChangeNumErrors(Object.keys(errors).length);
  }, [errors, onChangeNumErrors]);

  useEffect(() => {
    onChangeNumHighlightedFields(numHighlightedFields);
  }, [numHighlightedFields, onChangeNumHighlightedFields]);

  const onValidSubmit: SubmitHandler<MeterFormData> = async (data) => {
    setHasSubmitSucceeded(false);

    try {
      await (modeProps.mode === "create"
        ? onSubmitCreate(data)
        : onSubmitUpdate(data, modeProps.wizardObject.id));
      setHasSubmitSucceeded(true);
    } catch (error) {
      setErrorsFromResponseData<MeterFormData>(
        showErrorToast,
        error,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    }
  };

  const submitCallback = handleSubmit(onValidSubmit);

  function handleClickCancel() {
    reset();
    onCancel();
  }

  const isEdit = modeProps.mode === "edit";

  const globalIdField = (
    <FormFieldController
      control={control}
      data={formFieldData.global_id}
      error={errors.global_id}
    />
  );
  const nameField = (
    <FormFieldController
      control={control}
      data={formFieldData.name}
      error={errors.name}
    />
  );
  const mediumField = (
    <FormFieldController
      control={control}
      data={{ ...formFieldData.medium, disabled: isEdit }}
      error={errors.medium}
    />
  );
  const measurementTypeField = (
    <FormFieldController
      control={control}
      data={{ ...formFieldData.measurement_type, disabled: isEdit }}
      error={errors.measurement_type}
    />
  );
  const frequencyField = !isArbitraryCumulative && (
    <FormFieldController
      control={control}
      data={{ ...formFieldData.frequency, disabled: isEdit }}
      error={errors.frequency}
    />
  );
  const meteringDirectionField = (
    <FormFieldController
      control={control}
      data={{ ...formFieldData.metering_direction, disabled: isEdit }}
      error={errors.metering_direction}
    />
  );
  const locationFeedinField = (
    <CreatableFormFieldController
      control={control}
      data={{
        ...formFieldData.location_feedin,
        data: locationChoices
      }}
      setValue={setValue}
      target={locationTarget}
    />
  );
  const locationFeedoutField = (
    <CreatableFormFieldController
      control={control}
      data={{
        ...formFieldData.location_feedout,
        data: locationChoices
      }}
      setValue={setValue}
      target={locationTarget}
    />
  );
  const numberField = (
    <FormFieldController
      control={control}
      data={formFieldData.number}
      error={errors.number}
    />
  );
  const subMeteringSystemField = (
    <FormFieldController
      control={control}
      data={formFieldData.sub_metering_system}
      error={errors.sub_metering_system}
    />
  );
  const isCalibratedField = (
    <FormFieldController
      control={control}
      data={formFieldData.is_calibrated}
      error={errors.is_calibrated}
    />
  );
  const calibrationValidUntilField = (
    <FormFieldController
      control={control}
      data={formFieldData.calibration_valid_until}
      error={errors.calibration_valid_until}
    />
  );
  const conversionFactorField = isArbitraryCumulative && (
    <FormFieldController
      control={control}
      data={formFieldData.conversion_factor}
      error={errors.conversion_factor}
    />
  );

  return (
    <form className="MeterForm" onSubmit={submitCallback}>
      {isEdit ? (
        <>
          <FormSection label="Allgemeine Daten" mt="md">
            {showStaffView && globalIdField}
            {nameField}
            {mediumField}
            {measurementTypeField}
            {frequencyField}
            {meteringDirectionField}
            {calibrationValidUntilField}
            {conversionFactorField}
          </FormSection>
          <FormSection
            helpText={METERING_MSB_HELP_TEXT}
            label="Identifikation des Zähler im Messstellenbetrieb"
            mt="xl"
          >
            {locationFeedinField}
            {locationFeedoutField}
          </FormSection>
          <FormSection
            helpText={METERING_COMPANY_HELP_TEXT}
            label="Identifikation des Zähler im Eigenbetrieb"
            mt="xl"
          >
            {numberField}
            {subMeteringSystemField}
            {isCalibratedField}
          </FormSection>
        </>
      ) : (
        <>
          {nameField}
          {mediumField}
          {measurementTypeField}
          {frequencyField}
          {meteringDirectionField}
          {locationFeedinField}
          {locationFeedoutField}
          {numberField}
          {subMeteringSystemField}
          {isCalibratedField}
          {calibrationValidUntilField}
          {conversionFactorField}
        </>
      )}

      <ModalFooter>
        <Button
          disabled={isSubmitting || hasSubmitSucceeded}
          variant="default"
          onClick={handleClickCancel}
        >
          Abbrechen
        </Button>
        <Button
          color="secondary"
          loading={isSubmitting || hasSubmitSucceeded}
          onClick={submitCallback}
        >
          {modeProps.mode === "edit" ? "Speichern" : "Erstellen"}
        </Button>
      </ModalFooter>
    </form>
  );
}

export { MeterForm, MeterFormProps };
